<script setup lang="ts">
import { computed } from "vue"

import useAuth from "@Composables/useAuth.js"
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts"

//
// Props
interface Props {
    org: Organization
}
const props = defineProps<Props>()

//
// Setup
const { isLoggingOut, doLogout } = useAuth()

//
// Data
const navLinks = {
    organization: [
        {
            name: "Organization Settings",
            link: "org.settings.profile",
            icon: "fa-building",
            testSelector: "_org_settings",
            can: () => props.org.can([ "archive_organization", "create_organization", "update_organization" ]),
        },
        {
            name: "Users",
            link: "org.settings.profile.users",
            icon: "fa-users",
            testSelector: "_users",
            can: () => props.org.can("add_org_user"),
        },
        {
            name: "Branded Domains",
            link: "org.settings.profile.domains",
            icon: "fa-globe",
            testSelector: "_domains",
            can: () => props.org.can("manage_org_domains"),
        },
        {
            name: "API Tokens",
            link: "org.settings.teams.api",
            icon: "fa-key",
            testSelector: "_api_tokens",
            can: () => props.org.can("use_api"),
        },
        {
            name: "Subscription",
            link: "org.settings.profile.subscription",
            icon: "fa-credit-card",
            testSelector: "_subscription",
            can: () => props.org.can("manage_subscriptions"),
        },
        {
            name: "Payment & Invoices",
            link: "org.settings.profile.payment",
            icon: "fa-file-invoice-dollar",
            testSelector: "_payment",
            can: () => props.org.can("manage_subscriptions"),
        },
        {
            name: "Orders",
            link: "org.settings.profile.orders",
            icon: "fa-history",
            testSelector: "_orders",
            can: () => props.org.can("manage_subscriptions"),
        },
    ],
    account: [
        {
            name: "Profile",
            link: "org.settings.user.profile",
            icon: "fa-user",
            testSelector: "_profile",
            can: () => true,
        },
        {
            name: "Security",
            link: "org.settings.user.security",
            icon: "fa-shield-halved",
            testSelector: "_security",
            can: () => true,
        },
    ],
}

//
// Computed
const organizationLinks = computed(() =>
    navLinks.organization.filter((item) => item.can()),
)

const accountLinks = computed(() =>
    navLinks.account.filter((item) => item.can()),
)

//
// Methods
const handleLogout = () => {
    doLogout()
}

//
// Exposed Test Vars
defineExpose({ organizationLinks, accountLinks, isLoggingOut })
</script>

<template>
  <nav class="org-settings-sidebar _sidebar">
    <div class="org-settings-sidebar__header">
      <h3 class="org-settings-sidebar__title">
        Settings
      </h3>
    </div>

    <div
      v-if="organizationLinks.length !== 0"
      class="org-settings-sidebar__section _org_section"
    >
      <h4 class="org-settings-sidebar__section-title">
        Organization
      </h4>

      <router-link
        v-for="link in organizationLinks"
        :key="link.link"
        :to="{
          name: link.link,
          params: {
            org: props.org.slug,
          },
        }"
        class="settings-link-exact hover-white text-dark text-no-underline"
        :class="link.testSelector"
      >
        <i
          class="fa fa-fw mr-2"
          :class="link.icon"
        />
        {{ link.name }}
      </router-link>
    </div>

    <div class="org-settings-sidebar__section">
      <h4 class="org-settings-sidebar__section-title">
        Account
      </h4>

      <router-link
        v-for="link in accountLinks"
        :key="link.link"
        :to="{
          name: link.link,
          params: {
            org: props.org.slug,
          },
        }"
        class="settings-link-exact hover-white text-dark text-no-underline"
        :class="link.testSelector"
      >
        <i
          class="fa fa-fw mr-2"
          :class="link.icon"
        />
        {{ link.name }}
      </router-link>
    </div>

    <div class="org-settings-sidebar__footer">
      <button
        type="button"
        class="_logout btn btn-block btn-blue"
        :disabled="isLoggingOut"
        @click="handleLogout"
      >
        Log Out
        <i
          v-show="isLoggingOut"
          class="fa fa-spinner fa-spin"
        />
      </button>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
@import "@Sass/bootstrap/customization/colors";

.org-settings-sidebar {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__header {
        padding: 1rem;
    }

    &__title {
        margin: 0;
        font-weight: 600;
        color: $black;
    }

    &__section {
        margin-bottom: 1rem;

        :deep(.settings-link-exact) {
            display: block;
            padding: 0.75rem 1rem;
        }
    }

    &__section-title {
        padding: 0.5rem 1rem;
        margin: 0;
        color: $gray-500;
        font-size: 0.875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    &__footer {
        margin-top: auto;
        padding: 1rem;
    }
}
</style>
